/** @jsxImportSource @emotion/react */
import { useMediaQuery, useTheme } from '@mui/material';
import { DemoBanner, Drawer, DrawerRef, Header } from 'components';
import { useMerchantData } from 'contexts';
import {
  MerchantClassSettingType,
  MerchantProfileStepStatus,
  MerchantRoleGuid,
  PageRoute,
  PermissionEnum,
} from 'enums';
import { useScrollBottom } from 'hooks';
import {
  AccountPage,
  ApplicationPage,
  CliInvitePage,
  CreateOrderPage,
  CustomersPage,
  DashboardPage,
  DisbursementPage,
  IntegrationsPage,
  MarketingPage,
  NotificationSettingsPage,
  OffersPage,
  OrdersPage,
  PosMaterialsPage,
  ProductsConfigPage,
  SettingsPage,
  SetupGuidePage,
  StoreDirectoryPage,
  UsersPage,
} from 'pages';
import { FC, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { isProduction, isSandbox } from '../constants';
import { theme } from '../theme';
import * as styles from './DashboardLayout.styles';

interface LocationState {
  applicationComplete: string;
}

export interface DashboardLayoutProps {
  openHelpCentre: () => void;
}

const DashboardLayout: FC<DashboardLayoutProps> = ({
  openHelpCentre,
}): JSX.Element => {
  const { scrollBottom, scrollProps } = useScrollBottom();

  const {
    checkPermission,
    checkPermissions,
    checkMerchantDashboardSetting,
    hasRole,
    isAdmin,
    profileDataIsLoading,
    merchantProfile,
  } = useMerchantData();

  const location = useLocation();
  const locationState = location.state as LocationState;

  const shouldForceApplicationView =
    merchantProfile &&
    !merchantProfile?.profile?.submitted &&
    merchantProfile?.contact !== MerchantProfileStepStatus.Completed &&
    !locationState?.applicationComplete;

  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down(theme.breaks.sm));

  // ## Drawer management ##
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(
    window.innerWidth > theme.breaks.md + 1
  );
  const [isInnerDrawerOpen, setIsInnerDrawerOpen] = useState<boolean>(false);

  const canShowSpotlight = !isMobile && isDrawerOpen;

  const openDrawer = (): void => {
    setIsDrawerOpen(true);
    setIsInnerDrawerOpen(false);
  };
  const closeDrawer = (): void => {
    setIsDrawerOpen(false);
    setIsInnerDrawerOpen(false);
  };
  const openInnerDrawer = (): void => {
    setIsDrawerOpen(false);
    setIsInnerDrawerOpen(true);
  };

  const drawer = useRef<DrawerRef>(null);

  const resetInnerSelectedMenuItem = (): void => {
    drawer.current?.resetInnerSelectedMenuItem();
  };

  // ## JSX ##
  return (
    <>
      <Header
        openDrawer={openDrawer}
        closeDrawer={closeDrawer}
        isDrawerOpen={isDrawerOpen || isInnerDrawerOpen}
        openHelpCentre={openHelpCentre}
      />
      {!isProduction && !shouldForceApplicationView && <DemoBanner />}
      <div
        id="body-container"
        css={styles.bodyContainer(isDrawerOpen || isInnerDrawerOpen)}
      >
        <Drawer
          isDrawerOpen={isDrawerOpen}
          isInnerDrawerOpen={isInnerDrawerOpen}
          canShowSpotlight={canShowSpotlight}
          setIsDrawerOpen={setIsDrawerOpen}
          setIsInnerDrawerOpen={setIsInnerDrawerOpen}
        />
        <main css={styles.main} {...scrollProps}>
          <Routes>
            <Route
              path="/"
              element={
                shouldForceApplicationView ? (
                  <Navigate
                    to={PageRoute.Application}
                    replace
                    state={{ redirected: true }}
                  />
                ) : (
                  <DashboardPage />
                )
              }
            />

            {(!isProduction ||
              merchantProfile?.settlement !==
                MerchantProfileStepStatus.Completed) && (
              <>
                <Route
                  path={PageRoute.Application}
                  element={<ApplicationPage />}
                />
              </>
            )}

            {!isSandbox && (
              <Route
                path={PageRoute.SetupGuide}
                element={<SetupGuidePage openHelpCentre={openHelpCentre} />}
              />
            )}

            <Route
              path={PageRoute.CreateOrder}
              element={
                checkPermission(PermissionEnum.CreateOrderExecute) ? (
                  <CreateOrderPage
                    innerDrawerCallback={openDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                ) : (
                  <Navigate to={PageRoute.Orders} />
                )
              }
            />

            <Route
              path={`${PageRoute.Orders}/expires-soon`}
              element={<Navigate to={PageRoute.ExpiringOrders} />}
            />

            <Route
              path={PageRoute.Orders}
              element={
                (checkPermission(PermissionEnum.OrdersRead) && (
                  <OrdersPage
                    innerDrawerCallback={openInnerDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Orders}/:orderId`}
              element={
                (checkPermission(PermissionEnum.OrdersRead) && (
                  <OrdersPage
                    innerDrawerCallback={openInnerDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Orders}/status/:status`}
              element={
                (checkPermission(PermissionEnum.OrdersRead) && (
                  <OrdersPage
                    innerDrawerCallback={openInnerDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                )) || <Navigate to={PageRoute.Home} />
              }
            />
            <Route
              path={`${PageRoute.Orders}/status/:status/:orderId`}
              element={
                (checkPermission(PermissionEnum.OrdersRead) && (
                  <OrdersPage
                    innerDrawerCallback={openInnerDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={PageRoute.Customers}
              element={
                (checkPermission(PermissionEnum.CustomersRead) && (
                  <CustomersPage
                    innerDrawerCallback={openInnerDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Customers}/consumer/:consumerId`}
              element={
                (checkPermission(PermissionEnum.CustomersRead) && (
                  <CustomersPage
                    innerDrawerCallback={openInnerDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Customers}/status/:status`}
              element={
                (checkPermission(PermissionEnum.CustomersRead) && (
                  <CustomersPage
                    innerDrawerCallback={openInnerDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Customers}/status/:status/consumer/:consumerId`}
              element={
                (checkPermission(PermissionEnum.CustomersRead) && (
                  <CustomersPage
                    innerDrawerCallback={openInnerDrawer}
                    innerSelectedItemReset={resetInnerSelectedMenuItem}
                  />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Customers}/cli-invite`}
              element={
                (checkPermission(PermissionEnum.CustomersRead) &&
                  ((checkMerchantDashboardSetting(
                    MerchantClassSettingType.CLIInvitationEnabled
                  ) && (
                    <CustomersPage
                      innerDrawerCallback={openInnerDrawer}
                      innerSelectedItemReset={resetInnerSelectedMenuItem}
                    />
                  )) || <Navigate to={PageRoute.Customers} />)) || (
                  <Navigate to={PageRoute.Home} />
                )
              }
            />
            <Route
              path={`${PageRoute.Customers}/cli-invite/status/:status`}
              element={
                (checkPermission(PermissionEnum.CustomersRead) &&
                  ((checkMerchantDashboardSetting(
                    MerchantClassSettingType.CLIInvitationEnabled
                  ) && (
                    <CustomersPage
                      innerDrawerCallback={openInnerDrawer}
                      innerSelectedItemReset={resetInnerSelectedMenuItem}
                    />
                  )) || <Navigate to={PageRoute.Customers} />)) || (
                  <Navigate to={PageRoute.Home} />
                )
              }
            />

            <Route
              path={`${PageRoute.Tools}${PageRoute.RepaymentCalculator}`}
              element={
                checkPermission(PermissionEnum.CreateOrderExecute) &&
                checkMerchantDashboardSetting(
                  MerchantClassSettingType.EnableRepaymentCalculator
                ) ? (
                  <Navigate to={PageRoute.CreateOrder} />
                ) : (
                  <Navigate to={PageRoute.Home} />
                )
              }
            />

            <Route
              path={`${PageRoute.Tools}${PageRoute.CliInvite}`}
              element={
                (checkPermission(PermissionEnum.CreateInviteExecute) &&
                  checkMerchantDashboardSetting(
                    MerchantClassSettingType.CLIInvitationEnabled
                  ) && <CliInvitePage />) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={PageRoute.Settings}
              element={
                (checkPermissions([
                  PermissionEnum.NotificationsUpdate,
                  PermissionEnum.ApiIntegrationRead,
                  PermissionEnum.ReportsRead,
                  PermissionEnum.CustomS3ReportRead,
                  PermissionEnum.UserManagementExecute,
                ]) && <SettingsPage />) || <Navigate to={PageRoute.Home} />
              }
            />
            <Route
              path={`${PageRoute.Settings}${PageRoute.Account}`}
              element={
                (!isSandbox &&
                  (isAdmin || hasRole(MerchantRoleGuid.FranchiseManager)) && (
                    <AccountPage />
                  )) || <Navigate to={PageRoute.Home} />
              }
            />
            <Route
              path={`${PageRoute.Settings}${PageRoute.Notifications}`}
              element={
                (checkPermission(PermissionEnum.NotificationsUpdate) && (
                  <NotificationSettingsPage />
                )) || <Navigate to={PageRoute.Home} />
              }
            />
            <Route
              path={`${PageRoute.Settings}${PageRoute.Integration}`}
              element={
                (checkPermission(PermissionEnum.ApiIntegrationRead) && (
                  <IntegrationsPage />
                )) || <Navigate to={PageRoute.Home} />
              }
            />
            <Route
              path={`${PageRoute.Settings}${PageRoute.Disbursement}`}
              element={
                (checkPermissions([
                  PermissionEnum.ReportsRead,
                  PermissionEnum.CustomS3ReportRead,
                ]) && <DisbursementPage />) || <Navigate to={PageRoute.Home} />
              }
            />
            <Route
              path={`${PageRoute.Settings}${PageRoute.Users}`}
              element={
                (checkPermission(PermissionEnum.UserManagementExecute) && (
                  <UsersPage />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={PageRoute.Marketing}
              element={
                (checkPermissions([
                  PermissionEnum.StoreAdvertising,
                  PermissionEnum.StoreProfileRead,
                  PermissionEnum.StoreProfileUpdate,
                  PermissionEnum.StoreCategoryRead,
                  PermissionEnum.StoreCategoryUpdate,
                ]) && <MarketingPage />) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Marketing}${PageRoute.PosMaterials}`}
              element={
                (checkPermission(PermissionEnum.StoreAdvertising) && (
                  <PosMaterialsPage />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Marketing}${PageRoute.StoreDirectory}`}
              element={
                (checkPermission(PermissionEnum.StoreAdvertising) && (
                  <StoreDirectoryPage scrollBottom={scrollBottom} />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path={`${PageRoute.Marketing}${PageRoute.Campaigns}`}
              element={<Navigate to={PageRoute.Marketing} />}
            />

            <Route
              path={`${PageRoute.Marketing}${PageRoute.Offers}`}
              element={<OffersPage />}
            />

            <Route
              path={PageRoute.ProductConfig}
              element={
                (checkPermission(PermissionEnum.ProductConfigExecute) && (
                  <ProductsConfigPage />
                )) || <Navigate to={PageRoute.Home} />
              }
            />

            <Route
              path="*"
              element={
                !profileDataIsLoading && <Navigate to={PageRoute.Home} />
              }
            />
          </Routes>
        </main>
      </div>
    </>
  );
};

export default DashboardLayout;
